// src/apollo/retryRefreshLink.js

import { ApolloLink, Observable } from "@apollo/client";
import { performRefresh } from "../utils/refreshToken";
import { authStore } from "../utils/tokenStore";
import { logOut } from "../utils/functions";

const devLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log(...args);
  }
};

const looksLikeJwtProblem = (msg = "") => {
  const m = msg.toLowerCase();
  return (
    m.includes("expired token") ||
    m.includes("invalid-secret-key") ||
    m.includes("invalid token") ||
    m.includes("jwt") ||
    m.includes("signature")
  );
};

/**
 * In production WPGraphQL hides JWT details and returns generic server errors.
 * We treat these as immediate auth failures when the user is authenticated,
 * because a refresh attempt would return the same error and waste time.
 */
const looksLikeProductionAuthError = (msg = "") => {
  const m = msg.toLowerCase();
  return (
    m.includes("internal server error") ||
    m.includes("internal error") ||
    m.includes("server error")
  );
};

const immediateLogout = (reason) => {
  devLog(`🚨 retryRefreshLink → immediate logout: ${reason}`);
  authStore.clear();
  logOut();
};

/**
 * Apollo retry link — FINAL version
 */
export const retryRefreshLink = (getClient) =>
  new ApolloLink((operation, forward) => {
    const context = operation.getContext();

    // Do not retry refreshToken mutation
    if (context.skipRetryLink) {
      devLog("⏭️ retryRefreshLink: skipping refresh mutation");
      return forward(operation);
    }

    return new Observable((observer) => {
      let subscription;

      const runOperation = () => {
        subscription = forward(operation).subscribe({
          // -----------------------------------------------------
          // SUCCESSFUL RESPONSE (HTTP 200) — but may contain GraphQL errors
          // -----------------------------------------------------
          next: async (result) => {
            devLog("📦 retryRefreshLink → next() result:", result);

            const errorList = result?.errors || [];
            if (errorList.length > 0) {
              const errorText = JSON.stringify(errorList).toLowerCase();
              devLog("🧩 GraphQL response errors:", errorText);

              // Check for explicit JWT errors first → attempt refresh
              if (looksLikeJwtProblem(errorText)) {
                devLog("🔴 JWT error inside response → attempting refresh");

                const client = getClient();
                const newToken = await performRefresh(client);

                if (!newToken) {
                  devLog("🔥 Refresh failed → logout");
                  immediateLogout("refresh returned no token");
                  observer.error(errorList);
                  return;
                }

                devLog("🔄 Refresh succeeded → retrying operation");

                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${newToken}`,
                  },
                }));

                forward(operation).subscribe(observer);
                return;
              }

              // Check for production server errors on authenticated requests.
              // WPGraphQL returns "Internal server error" instead of JWT details
              // when debug mode is off. Only treat as auth failure when the user
              // has a stored token — unauthenticated server errors pass through.
              const auth = authStore.load();
              if (
                looksLikeProductionAuthError(errorText) &&
                auth?.authToken &&
                auth?.refreshToken
              ) {
                immediateLogout(
                  "Internal server error on authenticated request"
                );
                observer.error(errorList);
                return;
              }
            }

            observer.next(result);
          },

          // -----------------------------------------------------
          // NETWORK ERROR (actual failure)
          // -----------------------------------------------------
          error: async (error) => {
            devLog("❗ retryRefreshLink → network error:", error);

            const msg = (error?.message || "").toLowerCase();

            if (looksLikeJwtProblem(msg) || looksLikeProductionAuthError(msg)) {
              const auth = authStore.load();
              // Only treat server errors as auth failures when authenticated
              if (
                looksLikeJwtProblem(msg) ||
                (auth?.authToken && auth?.refreshToken)
              ) {
                immediateLogout(`network error: ${msg}`);
              }
            }

            observer.error(error);
          },

          // -----------------------------------------------------
          complete: () => observer.complete(),
        });
      };

      runOperation();

      return () => subscription?.unsubscribe();
    });
  });
